import { useLogout } from "@refinedev/core";
import { Camera, ChartColumn, Clapperboard, Film, GalleryVerticalEnd, Images, LayoutGrid, LogOut, Moon, Receipt, Send, Sparkles, TrendingUp, UserRound } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router";

import { Annotator } from "@/components/Annotator";
import { useIdentity } from "@/lib/identity";
import { loginPath } from "@/lib/loginReturn";

/**
 * Каркас панели (по образцу supply): слева меню, сверху хлебная крошка, справа
 * рабочая область — она и прокручивается.
 *
 * Порядок меню — порядок воронки: сначала ЧТО снимать (тренды и референсы),
 * потом КАК (проекты и кадры), библиотеки — общий склад для обоих.
 */
type NavItem = { label: string; icon: ReactNode; to: string; also: string[] };

const NAV: NavItem[] = [
  { label: "Тренды", icon: <TrendingUp size={14} />, to: "/trends", also: ["/trends/"] },
  { label: "Видео-референсы", icon: <Film size={14} />, to: "/video-refs", also: ["/video-refs/"] },
  { label: "Наши фото", icon: <Camera size={14} />, to: "/photos", also: ["/photos/"] },
  { label: "Персонажи", icon: <UserRound size={14} />, to: "/personas", also: ["/persona/"] },
  { label: "Проекты", icon: <Clapperboard size={14} />, to: "/projects", also: ["/project/"] },
  { label: "Библиотеки", icon: <Images size={14} />, to: "/lib", also: ["/lib/"] },
  { label: "Генерация", icon: <Sparkles size={14} />, to: "/generate", also: [] },
  { label: "Каталог", icon: <LayoutGrid size={14} />, to: "/catalog", also: [] },
  { label: "Результаты", icon: <GalleryVerticalEnd size={14} />, to: "/results", also: ["/results/"] },
  { label: "Публикации", icon: <Send size={14} />, to: "/posts", also: ["/posts/"] },
  { label: "Аналитика", icon: <ChartColumn size={14} />, to: "/analytics", also: ["/analytics/"] },
  { label: "Журнал вызовов", icon: <Receipt size={14} />, to: "/calls", also: ["/calls/"] },
];

function isActive(item: NavItem, pathname: string): boolean {
  return item.to === pathname || item.also.some((prefix) => pathname.startsWith(prefix));
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useIdentity();
  const { mutate: logout } = useLogout();
  const { pathname } = useLocation();
  const current = NAV.find((i) => isActive(i, pathname));
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (document.documentElement.dataset["theme"] as "dark" | "light") ?? "dark",
  );

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset["theme"] = next;
    setTheme(next);
  };

  return (
    <div className="grid h-screen grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)] overflow-hidden">
      <aside className="flex h-screen flex-col border-r border-border bg-bg-subtle">
        <div className="flex h-[var(--spacing-topbar)] flex-none items-center gap-2 border-b border-border px-3">
          <div className="grid size-[22px] flex-none place-items-center rounded-[5px] bg-fg text-[12px] font-semibold text-bg">
            R
          </div>
          <div className="text-[14px] font-semibold tracking-[-0.015em]">reels</div>
          <div className="text-[11.5px] text-fg-faint">контент-завод</div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          {NAV.map((item) => {
            const active = isActive(item, pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? "page" : undefined}
                className={
                  "flex h-8 w-full items-center gap-2.5 rounded-[var(--radius-ui)] px-2 text-[13.5px] transition-colors " +
                  (active ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:bg-bg-hover hover:text-fg")
                }
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="grid flex-none gap-2 border-t border-border p-2">
          <div className="flex items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1">
            <div className="size-[22px] flex-none rounded-full bg-accent" />
            <div className="min-w-0">
              <div className="truncate text-[12.5px]">{user?.name ?? "…"}</div>
              <div className="text-[11px] text-fg-faint">reels</div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              title="Выйти"
              className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-[var(--spacing-topbar)] flex-none items-center gap-3 border-b border-border bg-bg/85 px-5 backdrop-blur">
          <div className="flex min-w-0 items-center gap-2 text-[13.5px] text-fg-muted">
            <span>reels</span>
            <span className="text-border-strong">/</span>
            <span className="font-medium text-fg">{current?.label ?? "Тренды"}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              title="Тема"
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
            >
              <Moon size={14} />
            </button>
          </div>
        </header>

        {/* Экран — после личности: `null` — это 401, место человеку на входе. */}
        {user ? (
          <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
        ) : user === null ? (
          <Navigate to={loginPath()} replace />
        ) : (
          <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 text-[12.5px] text-fg-muted">
            {isPending ? "…" : "Не удалось узнать, кто вошёл, — обновите страницу."}
          </main>
        )}
      </div>

      <Annotator />
    </div>
  );
}
